/**
 * Applies light/dark scheme (system + manual, TZ 8.12.3) and the optional company primary colour (8.12.2) to the
 * document root. The brand colour is applied per scheme only when it passes WCAG AA against that scheme's card.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { checkPrimaryColorContrast, isHexColor } from '../tokens/color_contrast';
import { STATIC_THEME_TOKENS, type ColorScheme, type ThemeTokenValues } from '../tokens/design_tokens';
import { ThemeContext, type ThemeContextValue, type ThemePreference } from './theme_context';

const THEME_PREFERENCE_STORAGE_KEY = 'pc.theme-preference';
const DARK_SCHEME_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function readStoredPreference(): ThemePreference {
  try {
    const storedValue = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    if (storedValue === 'light' || storedValue === 'dark' || storedValue === 'system') {
      return storedValue;
    }
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the system preference is the default then.
  }
  return 'system';
}

function writeStoredPreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
  } catch {
    // Not persisting the toggle is acceptable; the current session still uses it.
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_SCHEME_MEDIA_QUERY).matches;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Company primary colour from /auth/me branding; ignored when it fails the contrast check. */
  brandPrimaryColor?: string | null;
}

export function ThemeProvider({ children, brandPrimaryColor = null }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemIsDark, setSystemIsDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQueryList = window.matchMedia(DARK_SCHEME_MEDIA_QUERY);
    const handleSchemeChange = (changeEvent: MediaQueryListEvent) => {
      setSystemIsDark(changeEvent.matches);
    };
    mediaQueryList.addEventListener('change', handleSchemeChange);
    return () => {
      mediaQueryList.removeEventListener('change', handleSchemeChange);
    };
  }, []);

  const resolvedScheme: ColorScheme =
    preference === 'system' ? (systemIsDark ? 'dark' : 'light') : preference;

  const appliedBrandPrimary = useMemo(() => {
    if (brandPrimaryColor === null || !isHexColor(brandPrimaryColor)) {
      return null;
    }
    const contrastReport = checkPrimaryColorContrast(
      brandPrimaryColor,
      STATIC_THEME_TOKENS[resolvedScheme].card,
    );
    return contrastReport.passesWcagAa ? contrastReport : null;
  }, [brandPrimaryColor, resolvedScheme]);

  // Canvas charts need concrete colours; they equal the CSS tokens of the scheme plus the accepted brand colour.
  const tokens = useMemo<ThemeTokenValues>(
    () => ({
      ...STATIC_THEME_TOKENS[resolvedScheme],
      primary: appliedBrandPrimary?.primaryColor ?? STATIC_THEME_TOKENS[resolvedScheme].primary,
    }),
    [resolvedScheme, appliedBrandPrimary],
  );

  useEffect(() => {
    const rootElement = document.documentElement;
    if (preference === 'system') {
      rootElement.removeAttribute('data-theme');
    } else {
      rootElement.setAttribute('data-theme', preference);
    }
    if (appliedBrandPrimary === null) {
      rootElement.style.removeProperty('--primary');
      rootElement.style.removeProperty('--primary-ink');
      rootElement.style.removeProperty('--focus-ring');
      rootElement.style.removeProperty('--action');
      rootElement.style.removeProperty('--action-ink');
      rootElement.style.removeProperty('--action-hover');
    } else {
      rootElement.style.setProperty('--primary', appliedBrandPrimary.primaryColor);
      rootElement.style.setProperty('--primary-ink', appliedBrandPrimary.buttonTextColor);
      rootElement.style.setProperty('--focus-ring', appliedBrandPrimary.primaryColor);
      // Solid buttons use the accepted brand colour in both schemes (it passed WCAG AA with its label colour).
      rootElement.style.setProperty('--action', appliedBrandPrimary.primaryColor);
      rootElement.style.setProperty('--action-ink', appliedBrandPrimary.buttonTextColor);
      rootElement.style.setProperty(
        '--action-hover',
        `color-mix(in srgb, ${appliedBrandPrimary.primaryColor} 88%, #000000)`,
      );
    }
  }, [preference, appliedBrandPrimary]);

  const setPreference = useCallback((nextPreference: ThemePreference) => {
    writeStoredPreference(nextPreference);
    setPreferenceState(nextPreference);
  }, []);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      preference,
      setPreference,
      resolvedScheme,
      tokens,
      appliedBrandPrimary: appliedBrandPrimary?.primaryColor ?? null,
    }),
    [preference, setPreference, resolvedScheme, tokens, appliedBrandPrimary],
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}
